import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Typography, Paper, TextField, Button, Alert, CircularProgress, Grid,
  MenuItem, FormControl, InputLabel, Select, Switch, FormControlLabel, Tabs, Tab,
  Radio, RadioGroup, Checkbox, FormGroup, FormLabel
} from '@mui/material';
import { SketchPicker } from 'react-color';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// API Endpoints
const API_ENDPOINTS = {
  APPLICATION_TYPES: {
    BASE: '/api/application-setting-types'
  },
  APPLICATION_SETTINGS: {
    BASE: '/api/application-settings',
    BY_TYPE: (slug) => `/api/application-settings/type/${slug}`
  }
};

// FileUpload Component (unchanged)
const FileUpload = ({ label, currentImage, onFileSelect, imagePath }) => {
  const [preview, setPreview] = useState(currentImage ? `${imagePath}${currentImage}` : null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      onFileSelect(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  return (
    <Box>
      <FormLabel>{label}</FormLabel>
      <input type="file" accept="image/*" onChange={handleFileChange} />
      {preview && (
        <Box mt={1}>
          <img src={preview} alt={label} style={{ maxWidth: '100px', maxHeight: '100px' }} />
        </Box>
      )}
    </Box>
  );
};

// MultipleFileUpload Component (unchanged)
const MultipleFileUpload = ({ label, currentImages, onFilesSelect, imagePath, onRemoveImage }) => {
  const [previews, setPreviews] = useState(
    currentImages ? currentImages.map(img => `${imagePath}${img}`) : []
  );

  const handleFilesChange = (e) => {
    const files = Array.from(e.target.files);
    onFilesSelect(files);
    setPreviews(files.map(file => URL.createObjectURL(file)));
  };

  return (
    <Box>
      <FormLabel>{label}</FormLabel>
      <input type="file" accept="image/*" multiple onChange={handleFilesChange} />
      <Box mt={1} display="flex" flexWrap="wrap" gap={2}>
        {previews.map((preview, index) => (
          <Box key={index} position="relative">
            <img src={preview} alt={`${label}-${index}`} style={{ maxWidth: '100px', maxHeight: '100px' }} />
            <Button
              size="small"
              color="error"
              onClick={() => {
                onRemoveImage(index);
                setPreviews(prev => prev.filter((_, i) => i !== index));
              }}
            >
              Remove
            </Button>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const ApplicationSettings = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [settingType, setSettingType] = useState(null);
  const [settings, setSettings] = useState([]);
  const [fields, setFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [selectedTab, setSelectedTab] = useState(0);
  const [settingTypes, setSettingTypes] = useState([]);
  const formRefs = useRef({});

  // Fetch all data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch setting types
        const typesResponse = await axios.get(API_ENDPOINTS.APPLICATION_TYPES.BASE, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (typesResponse.data.success) {
          const typesWithSettings = typesResponse.data.data.filter(type => type.applicationSettings?.length > 0);
          setSettingTypes(typesWithSettings);

          // Set selected tab and fetch settings
          const selectedTypeSlug = type || (typesWithSettings.length > 0 ? typesWithSettings[0].slug : null);
          if (selectedTypeSlug) {
            const typeIndex = typesWithSettings.findIndex(t => t.slug === selectedTypeSlug);
            setSelectedTab(typeIndex !== -1 ? typeIndex : 0);

            // Fetch settings for the selected type
            const settingsResponse = await axios.get(API_ENDPOINTS.APPLICATION_SETTINGS.BY_TYPE(selectedTypeSlug), {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            if (settingsResponse.data.success) {
              setSettingType(settingsResponse.data.data.type);
              setSettings(settingsResponse.data.data.settings);
              setFields(settingsResponse.data.data.settings);
              const initialFormData = {};
              settingsResponse.data.data.settings.forEach(setting => {
                if (setting.input_type === 'checkbox') {
                  initialFormData[`checkbox${setting.id}`] = setting.value ? setting.value.split(',') : [];
                } else if (setting.input_type === 'switch') {
                  initialFormData[`switch-${setting.id}`] = setting.value === '1' || setting.value === true;
                } else {
                  initialFormData[setting.id] = setting.value || '';
                }
                if (setting.input_type === 'file') {
                  initialFormData[`alt_text${setting.id}`] = setting.alt_text || '';
                }
              });
              setFormData(initialFormData);
            } else {
              setError(settingsResponse.data.message || 'Failed to fetch settings');
            }
          }
        } else {
          setError('Failed to fetch setting types');
        }
      } catch (error) {
        setError(error.response?.data?.message || 'An error occurred while fetching data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [type]);

  // Apply dynamic classes
  useEffect(() => {
    Object.keys(formRefs.current).forEach(key => {
      const { label, parent, grandparent } = formRefs.current[key];
      if (label === 'Opening Hours Title' && parent) {
        parent.classList.add('opening-hours-title-full');
      }
      if (label === 'Clinic Info Sub Title' && grandparent) {
        grandparent.classList.add('customize-general-settings');
      }
    });
  }, [settings]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    const typeSlug = settingTypes[newValue]?.slug;
    if (typeSlug) {
      navigate(`/application-settings/type/${typeSlug}`);
    }
  };

  // Handle input changes
  const handleInputChange = (settingId, value, inputType = null) => {
    setFormData(prev => {
      const newData = { ...prev };
      if (inputType === 'checkbox') {
        const currentValues = prev[`checkbox${settingId}`] || [];
        if (currentValues.includes(value)) {
          newData[`checkbox${settingId}`] = currentValues.filter(item => item !== value);
        } else {
          newData[`checkbox${settingId}`] = [...currentValues, value];
        }
      } else {
        newData[settingId] = value;
      }
      return newData;
    });
  };

  // Handle file upload
  const handleFileUpload = (settingId, file) => {
    setFormData(prev => ({
      ...prev,
      [settingId]: file
    }));
  };

  // Handle multiple files upload
  const handleMultipleFilesUpload = (settingId, files) => {
    setFormData(prev => ({
      ...prev,
      [settingId]: files
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const submitData = new FormData();
      submitData.append('setting_type_id', settingType.id);
      submitData.append('setting_type_slug', settingType.slug);
      Object.keys(formData).forEach(key => {
        if (formData[key] instanceof File) {
          submitData.append(key, formData[key]);
        } else if (Array.isArray(formData[key])) {
          submitData.append(key, formData[key].join(','));
        } else if (key.startsWith('switch-')) {
          submitData.append(key, formData[key] ? '1' : '0');
        } else {
          submitData.append(key, formData[key]);
        }
      });
      const response = await axios.post(API_ENDPOINTS.APPLICATION_SETTINGS.BASE, submitData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      if (response.data.success) {
        setSuccess('Settings saved successfully');
        const refreshResponse = await axios.get(API_ENDPOINTS.APPLICATION_SETTINGS.BY_TYPE(settingType.slug), {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (refreshResponse.data.success) {
          setSettings(refreshResponse.data.data.settings);
          setFields(refreshResponse.data.data.settings);
          const updatedFormData = {};
          refreshResponse.data.data.settings.forEach(setting => {
            if (setting.input_type === 'checkbox') {
              updatedFormData[`checkbox${setting.id}`] = setting.value ? setting.value.split(',') : [];
            } else if (setting.input_type === 'switch') {
              updatedFormData[`switch-${setting.id}`] = setting.value === '1' || setting.value === true;
            } else {
              updatedFormData[setting.id] = setting.value || '';
            }
            if (setting.input_type === 'file') {
              updatedFormData[`alt_text${setting.id}`] = setting.alt_text || '';
            }
          });
          setFormData(updatedFormData);
        }
      } else {
        setError(response.data.message || 'Failed to save settings');
      }
    } catch (error) {
      setError(error.response?.data?.errors
        ? Object.values(error.response.data.errors).join(', ')
        : error.response?.data?.message || 'An error occurred while saving settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {settingTypes.map((type) => (
                <Tab key={type.slug} label={type.name} />
              ))}
            </Tabs>
          </Box>

          {settingType && (
            <Typography variant="h5" sx={{ mb: 3 }}>
              {settingType.type}
            </Typography>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {settings.map(setting => (
                <React.Fragment key={setting.id}>
                  {setting.input_type === 'heading' && (
                    <Grid item xs={12}>
                      <Typography variant="h6" component="h4">
                        {setting.field_name}
                      </Typography>
                    </Grid>
                  )}

                  {setting.input_type === 'color' && (
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth>
                        <FormLabel ref={el => {
                          if (el) {
                            formRefs.current[setting.id] = {
                              label: setting.field_name,
                              parent: el.parentElement,
                              grandparent: el.parentElement?.parentElement
                            };
                          }
                        }}>
                          {setting.field_name}
                        </FormLabel>
                        <SketchPicker
                          color={formData[setting.id] || '#fff'}
                          onChangeComplete={(color) => handleInputChange(setting.id, color.hex)}
                        />
                      </FormControl>
                    </Grid>
                  )}

                  {setting.input_type === 'textbox' && (
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label={setting.field_name}
                        value={formData[setting.id] || ''}
                        onChange={(e) => handleInputChange(setting.id, e.target.value)}
                        inputRef={el => {
                          if (el) {
                            formRefs.current[setting.id] = {
                              label: setting.field_name,
                              parent: el.parentElement,
                              grandparent: el.parentElement?.parentElement
                            };
                          }
                        }}
                      />
                    </Grid>
                  )}

                  {setting.input_type === 'select' && (
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth>
                        <InputLabel>{setting.field_name}</InputLabel>
                        <Select
                          value={formData[setting.id] || ''}
                          onChange={(e) => handleInputChange(setting.id, e.target.value)}
                          label={setting.field_name}
                          inputRef={el => {
                            if (el) {
                              formRefs.current[setting.id] = {
                                label: setting.field_name,
                                parent: el.parentElement,
                                grandparent: el.parentElement?.parentElement
                              };
                            }
                          }}
                        >
                          <MenuItem value="">Select {setting.field_name}</MenuItem>
                          {setting.options?.split(',').map(option => (
                            <MenuItem key={option.trim()} value={option.trim()}>
                              {option.trim()}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}

                  {setting.input_type === 'radio' && (
                    <Grid item xs={12} sm={4}>
                      <FormControl>
                        <FormLabel ref={el => {
                          if (el) {
                            formRefs.current[setting.id] = {
                              label: setting.field_name,
                              parent: el.parentElement,
                              grandparent: el.parentElement?.parentElement
                            };
                          }
                        }}>
                          {setting.field_name}
                        </FormLabel>
                        <RadioGroup
                          value={formData[`radio${setting.id}`] || ''}
                          onChange={(e) => handleInputChange(`radio${setting.id}`, e.target.value)}
                        >
                          {setting.options?.split(',').map(option => (
                            <FormControlLabel
                              key={option.trim()}
                              value={option.trim()}
                              control={<Radio />}
                              label={option.trim()}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                  )}

                  {setting.input_type === 'checkbox' && (
                    <Grid item xs={12} sm={4}>
                      <FormControl>
                        <FormLabel ref={el => {
                          if (el) {
                            formRefs.current[setting.id] = {
                              label: setting.field_name,
                              parent: el.parentElement,
                              grandparent: el.parentElement?.parentElement
                            };
                          }
                        }}>
                          {setting.field_name}
                        </FormLabel>
                        <FormGroup>
                          {setting.options?.split(',').map(option => (
                            <FormControlLabel
                              key={option.trim()}
                              control={
                                <Checkbox
                                  checked={(formData[`checkbox${setting.id}`] || []).includes(option.trim())}
                                  onChange={() => handleInputChange(setting.id, option.trim(), 'checkbox')}
                                />
                              }
                              label={option.trim()}
                            />
                          ))}
                        </FormGroup>
                      </FormControl>
                    </Grid>
                  )}

                  {setting.input_type === 'textarea-normal' && (
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <FormLabel ref={el => {
                          if (el) {
                            formRefs.current[setting.id] = {
                              label: setting.field_name,
                              parent: el.parentElement,
                              grandparent: el.parentElement?.parentElement
                            };
                          }
                        }}>
                          {setting.field_name}
                        </FormLabel>
                        <TextField
                          multiline
                          rows={4}
                          value={formData[setting.id] || ''}
                          onChange={(e) => handleInputChange(setting.id, e.target.value)}
                          fullWidth
                        />
                      </FormControl>
                    </Grid>
                  )}

                  {setting.input_type === 'textarea' && (
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <FormLabel ref={el => {
                          if (el) {
                            formRefs.current[setting.id] = {
                              label: setting.field_name,
                              parent: el.parentElement,
                              grandparent: el.parentElement?.parentElement
                            };
                          }
                        }}>
                          {setting.field_name}
                        </FormLabel>
                        <ReactQuill
                          value={formData[setting.id] || ''}
                          onChange={(value) => handleInputChange(setting.id, value)}
                          style={{ marginTop: '8px', minHeight: '200px' }}
                        />
                      </FormControl>
                    </Grid>
                  )}

                  {setting.input_type === 'file' && (
                    <>
                      <Grid item xs={12} sm={4}>
                        <FileUpload
                          label={setting.field_name}
                          currentImage={setting.value}
                          onFileSelect={(file) => handleFileUpload(setting.id, file)}
                          imagePath="/Uploads/application-settings/"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label={`${setting.field_name} Alt Text`}
                          value={formData[`alt_text${setting.id}`] || ''}
                          onChange={(e) => handleInputChange(`alt_text${setting.id}`, e.target.value)}
                          placeholder="Image Alt Text"
                          inputRef={el => {
                            if (el) {
                              formRefs.current[`alt_text${setting.id}`] = {
                                label: `${setting.field_name} Alt Text`,
                                parent: el.parentElement,
                                grandparent: el.parentElement?.parentElement
                              };
                            }
                          }}
                        />
                      </Grid>
                    </>
                  )}

                  {setting.input_type === 'multiple-files' && (
                    <Grid item xs={12}>
                      <MultipleFileUpload
                        label={setting.field_name}
                        currentImages={setting.value ? JSON.parse(setting.value) : []}
                        onFilesSelect={(files) => handleMultipleFilesUpload(setting.id, files)}
                        imagePath="/Uploads/application-settings/"
                        onRemoveImage={(index) => {
                          axios.delete(`/api/remove-multiple-image-item/${setting.id}/${index}`, {
                            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                          }).then(() => {
                            const updatedImages = JSON.parse(setting.value || '[]').filter((_, i) => i !== index);
                            setSettings(prev => prev.map(s => s.id === setting.id ? { ...s, value: JSON.stringify(updatedImages) } : s));
                            setFields(prev => prev.map(s => s.id === setting.id ? { ...s, value: JSON.stringify(updatedImages) } : s));
                          });
                        }}
                      />
                    </Grid>
                  )}

                  {setting.input_type === 'switch' && (
                    <Grid item xs={12} sm={4}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData[`switch-${setting.id}`] || false}
                            onChange={(e) => handleInputChange(`switch-${setting.id}`, e.target.checked)}
                          />
                        }
                        label={setting.field_name}
                        ref={el => {
                          if (el) {
                            formRefs.current[setting.id] = {
                              label: setting.field_name,
                              parent: el.parentElement,
                              grandparent: el.parentElement?.parentElement
                            };
                          }
                        }}
                      />
                    </Grid>
                  )}
                </React.Fragment>
              ))}
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ mb: 3 }}>
                  Application Type Fields
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <Box>
                  {fields.length > 0 ? (
                    <ul>
                      {fields.map(field => (
                        <li key={field.id}>
                          {field.field_name}: {field.value}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Typography>No fields found for this application type.</Typography>
                  )}
                </Box>
              </Paper>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
              >
                {saving ? <CircularProgress size={24} /> : 'Save'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/admin/dashboard')}
                disabled={saving}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ApplicationSettings;